/* eslint-disable @typescript-eslint/no-floating-promises */
import { formatUnits, ZeroAddress } from 'ethers'
import React, { FC, useCallback, useMemo } from 'react'
import { View } from 'react-native'

import { Network } from '@ambire-common/interfaces/network'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { BROADCAST_OPTIONS } from '@ambire-common/libs/broadcast/broadcast'
import CopyIcon from '@common/assets/svg/CopyIcon'
import OpenIcon from '@common/assets/svg/OpenIcon'
import RefreshIcon from '@common/assets/svg/RefreshIcon'
import SpeedUpIcon from '@common/assets/svg/SpeedUpIcon'
import Button from '@common/components/Button'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import { isWeb } from '@common/config/env'
import { useTranslation } from '@common/config/localization'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import useWindowSize from '@common/hooks/useWindowSize'
import { ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import { setStringAsync } from '@common/utils/clipboard'
import { getTransactionExplorerLink } from '@common/utils/explorerLink'
import { openInTab } from '@common/utils/links'
import { isExtension } from '@web/constants/browserapi'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

import { getSummaryBalanceChanges } from './helpers'
import getStyles from './styles'
import { SubmittedAccountOpLike } from './types'

type Props = {
  network: Network
  size: 'sm' | 'md' | 'lg'
  rawCalls?: SubmittedAccountOpLike['calls']
  submittedAccountOp: SubmittedAccountOpLike
  closeBottomSheet?: () => void
} & Pick<
  SubmittedAccountOpLike,
  'txnId' | 'identifiedBy' | 'accountAddr' | 'gasFeePayment' | 'status'
>

const increaseByFifteenPercent = (value: bigint) => (value * 115n + 99n) / 100n
const EXPLORER_LINKS_DISABLED_TOOLTIP =
  'Explorer links are on the right side of each transaction in transaction details'
const canBuildSpeedUpAccountOp = (
  submittedAccountOp: SubmittedAccountOpLike
): submittedAccountOp is SubmittedAccountOpLike &
  Required<
    Pick<
      SubmittedAccountOpLike,
      'signingKeyAddr' | 'signingKeyType' | 'nonce' | 'gasLimit' | 'signature'
    >
  > =>
  submittedAccountOp.signingKeyAddr !== undefined &&
  submittedAccountOp.signingKeyType !== undefined &&
  submittedAccountOp.nonce !== undefined &&
  submittedAccountOp.gasLimit !== undefined &&
  submittedAccountOp.signature !== undefined

const Footer: FC<Props> = ({
  network,
  txnId,
  rawCalls,
  submittedAccountOp,
  identifiedBy,
  accountAddr,
  gasFeePayment,
  status,
  closeBottomSheet
}) => {
  const { styles } = useTheme(getStyles)
  const { addToast } = useToast()
  const { account: selectedAccount } = useSelectedAccountControllerState()
  const { dispatch } = useBackgroundService()
  const { navigate } = useNavigation()
  const { t } = useTranslation()
  // Narrow phone-browser viewport (mobile-web). Build-time `isMobile` is false on
  // web, so detect via actual window width instead to keep the footer on one row.
  const { minWidthSize } = useWindowSize()
  const isNarrow = minWidthSize('s')

  // On the mobile-web SPA there is no auto-opening action window (the
  // browser-extension background opens one; the SPA has none), so dispatching
  // ADD_CALLS_USER_REQUEST builds the request but nothing navigates and the
  // button looks dead. Mirror PendingRequestsBanner: close the sheet and route
  // to the sign screen ourselves. No-op on the extension, where the background
  // opens the action window.
  const routeToSignOnSpa = useCallback(() => {
    if (!(isWeb && !isExtension)) return
    closeBottomSheet?.()
    navigate(ROUTES.signAccountOp)
  }, [navigate, closeBottomSheet])

  const { chainId } = network
  const isPendingTransaction =
    status === AccountOpStatus.Pending || status === AccountOpStatus.BroadcastedButNotConfirmed
  // the whole account op is a failure / success
  // or at least one call is a failure / success
  // means that there's at least one explorer transaction for checking
  const isMinedTransaction =
    status === AccountOpStatus.Failure ||
    status === AccountOpStatus.Success ||
    (submittedAccountOp.identifiedBy.type === 'MultipleTxns' &&
      submittedAccountOp.calls.find(
        (c) => c.status === AccountOpStatus.Failure || c.status === AccountOpStatus.Success
      ))
  const areExplorerButtonsDisabled =
    submittedAccountOp.identifiedBy.type === 'MultipleTxns' && submittedAccountOp.calls.length > 1
  const shouldShowSpeedUp =
    isPendingTransaction &&
    gasFeePayment?.broadcastOption !== BROADCAST_OPTIONS.byRelayer &&
    gasFeePayment?.broadcastOption !== BROADCAST_OPTIONS.byBundler &&
    canBuildSpeedUpAccountOp(submittedAccountOp)
  const isExternal = submittedAccountOp.activitySource === 'external'
  // Transactions initiated from a connected dApp (e.g. Liberty Swap in the
  // in-app browser) stamp each call with its `dapp` session at build time
  // (RequestsController.#buildUserRequestFromDAppRequest). Such txns must not
  // offer "Repeat" — replaying raw dApp calls outside the dApp's context is
  // meaningless/unsafe. Speed-up (accelerating a still-pending broadcast) stays.
  const isFromDapp = !!submittedAccountOp.calls?.some((call) => !!call.dapp)
  const canRepeatTransaction =
    !!rawCalls?.length && selectedAccount?.addr === accountAddr && !isExternal

  // Swap repeat: a confirmed swap shows up as one token leaving (assets out)
  // and another arriving (assets in). When both sides are present we can repeat
  // it by reopening the Swap & Bridge form pre-filled from the balance changes,
  // instead of re-running the raw calls on the Sign screen. This works for
  // external (reconstructed-after-reload) swaps too, where there is no internal
  // accountOp / meta.swapTxn to replay. Excludes the native gas/Gas-Tank leg so
  // the sold token (not the gas fee) is preselected.
  const { swapFromChange, swapToChange } = useMemo(() => {
    const balanceChanges = getSummaryBalanceChanges(submittedAccountOp)
    const outChanges = balanceChanges.filter(
      (c) => c.balanceChange < 0n && c.iconType !== 'gasTank' && c.address !== 'gas-tank'
    )
    const nonNativeOut = outChanges.find(
      (c) => c.address.toLowerCase() !== ZeroAddress.toLowerCase()
    )
    return {
      swapFromChange: nonNativeOut || outChanges[0] || null,
      swapToChange: balanceChanges.find((c) => c.balanceChange > 0n) || null
    }
  }, [submittedAccountOp])

  const canRepeatSwap =
    !!swapFromChange && !!swapToChange && selectedAccount?.addr === accountAddr
  const canRepeat = canRepeatTransaction || canRepeatSwap

  const explorerLink = getTransactionExplorerLink({
    txnId,
    chainId: Number(chainId),
    identifiedBy,
    explorerUrl: network.explorerUrl
  })

  const handleCopyTransaction = useCallback(() => {
    if (!chainId) {
      const message = t(
        "Can't copy the transaction link because the network information is missing."
      )
      addToast(message, { type: 'error' })

      return
    }

    setStringAsync(explorerLink)
    addToast(t('Copied to clipboard!') as string, { timeout: 2500 })
  }, [addToast, explorerLink, chainId, t])

  const handleRepeatTransaction = useCallback(() => {
    if (!rawCalls) return

    dispatch({
      type: 'REQUESTS_CONTROLLER_ADD_CALLS_USER_REQUEST',
      params: {
        userRequestParams: {
          calls: rawCalls,
          meta: { chainId: network.chainId, accountAddr }
        }
      }
    })
    routeToSignOnSpa()
  }, [rawCalls, dispatch, network.chainId, accountAddr, routeToSignOnSpa])

  // Reopen Swap & Bridge pre-filled with the old swap's tokens/amount. The swap
  // form reads these off the route navigation state on mount (see
  // useSwapAndBridgeForm), the same mechanism the "Retry" button on a failed
  // active route uses.
  const handleRepeatSwap = useCallback(() => {
    if (!swapFromChange) return

    closeBottomSheet?.()
    navigate(ROUTES.swapAndBridge, {
      state: {
        preselectedFromToken: {
          address: swapFromChange.address,
          chainId: swapFromChange.chainId
        },
        ...(swapToChange && {
          preselectedToToken: {
            address: swapToChange.address,
            chainId: swapToChange.chainId
          }
        }),
        fromAmount: formatUnits(-swapFromChange.balanceChange, swapFromChange.decimals)
      }
    })
  }, [swapFromChange, swapToChange, navigate, closeBottomSheet])

  const handleSpeedUpTransaction = useCallback(() => {
    if (!submittedAccountOp.gasFeePayment || !canBuildSpeedUpAccountOp(submittedAccountOp)) return

    const nextGasFeePayment = {
      ...submittedAccountOp.gasFeePayment,
      gasPrice: increaseByFifteenPercent(submittedAccountOp.gasFeePayment.gasPrice),
      maxPriorityFeePerGas:
        submittedAccountOp.gasFeePayment.maxPriorityFeePerGas &&
        submittedAccountOp.gasFeePayment.maxPriorityFeePerGas !== 0n
          ? increaseByFifteenPercent(submittedAccountOp.gasFeePayment.maxPriorityFeePerGas)
          : submittedAccountOp.gasFeePayment.maxPriorityFeePerGas
    }

    dispatch({
      type: 'REQUESTS_CONTROLLER_ADD_CALLS_USER_REQUEST',
      params: {
        // `accountOp` is consumed by ambire-common's RequestsController.build (speed-up
        // path), but ZKX's app-level action type only declares `calls`/`meta`. Cast to
        // forward it without widening the shared action type.
        userRequestParams: {
          calls: submittedAccountOp.calls,
          meta: {
            chainId: submittedAccountOp.chainId,
            accountAddr: submittedAccountOp.accountAddr
          },
          accountOp: {
            id: submittedAccountOp.id,
            accountAddr: submittedAccountOp.accountAddr,
            chainId: submittedAccountOp.chainId,
            signingKeyAddr: submittedAccountOp.signingKeyAddr,
            signingKeyType: submittedAccountOp.signingKeyType,
            nonce: submittedAccountOp.nonce,
            eoaNonce: submittedAccountOp.eoaNonce,
            calls: submittedAccountOp.calls,
            feeCall: submittedAccountOp.feeCall,
            activatorCall: submittedAccountOp.activatorCall,
            gasLimit: submittedAccountOp.gasLimit,
            signature: submittedAccountOp.signature,
            gasFeePayment: nextGasFeePayment,
            txnId: submittedAccountOp.txnId,
            asUserOperation: submittedAccountOp.asUserOperation,
            signers: submittedAccountOp.signers,
            signed: submittedAccountOp.signed,
            safeTx: submittedAccountOp.safeTx,
            flags: submittedAccountOp.flags,
            meta: {
              ...submittedAccountOp.meta,
              speedUp: {
                enabled: true
              }
            }
          }
        } as any
      }
    })
    routeToSignOnSpa()
  }, [submittedAccountOp, dispatch, routeToSignOnSpa])

  const handleOpenExplorer = useCallback(async () => {
    if (!chainId) {
      const message = t(
        "Can't open the transaction details because the transaction or network information is missing."
      )
      addToast(message, { type: 'error' })

      return
    }

    try {
      await openInTab({ url: explorerLink })
    } catch (e: any) {
      addToast(e?.message || 'Error opening explorer', { type: 'error' })
    }
  }, [addToast, chainId, explorerLink, t])

  if (!canRepeat && !isMinedTransaction) return null

  return (
    <View style={[styles.footer, isNarrow && styles.footerNarrow]}>
      <View style={[styles.footerButtonsRow, isNarrow && styles.footerButtonsRowNarrow]}>
        <View
          dataSet={createGlobalTooltipDataSet({
            id: `open-explorer-disabled-${submittedAccountOp.id}`,
            content: t(EXPLORER_LINKS_DISABLED_TOOLTIP),
            hidden: !areExplorerButtonsDisabled
          })}
        >
          <Button
            text={t('Open explorer')}
            type="outline"
            onPress={handleOpenExplorer}
            size="small"
            disabled={areExplorerButtonsDisabled}
            hasBottomSpacing={false}
            style={[styles.footerButton, isNarrow && styles.footerButtonNarrow]}
            childrenPosition="left"
            testID="view-transaction-link"
          >
            <OpenIcon style={spacings.mrTy} width={16} height={16} />
          </Button>
        </View>

        <View style={styles.footerRightButtonsGroup}>
          {/* dApp-initiated txns hide "Repeat" entirely (not just disable it);
              a pending dApp txn can still show "Speed up". */}
          {!(isFromDapp && !shouldShowSpeedUp) && (
          <View
            dataSet={createGlobalTooltipDataSet({
              id: `repeat-disabled-${submittedAccountOp.id}`,
              content: isExternal
                ? t('This transaction cannot be repeated')
                : t('Switch to this account to proceed'),
              hidden: canRepeat
            })}
          >
            <Button
              type="secondary"
              text={t(shouldShowSpeedUp ? 'Speed up' : 'Repeat')}
              onPress={
                // eslint-disable-next-line no-nested-ternary
                shouldShowSpeedUp
                  ? handleSpeedUpTransaction
                  : canRepeatSwap
                  ? handleRepeatSwap
                  : handleRepeatTransaction
              }
              size="small"
              hasBottomSpacing={false}
              disabled={!canRepeat}
              style={[styles.footerButton, spacings.mrTy, isNarrow && styles.footerButtonNarrow]}
              childrenPosition="left"
            >
              {shouldShowSpeedUp ? (
                <SpeedUpIcon style={spacings.mrTy} width={16} height={16} />
              ) : (
                <RefreshIcon style={spacings.mrTy} width={16} height={16} />
              )}
            </Button>
          </View>
          )}

          <View
            dataSet={createGlobalTooltipDataSet({
              id: `copy-explorer-link-disabled-${submittedAccountOp.id}`,
              content: t(EXPLORER_LINKS_DISABLED_TOOLTIP),
              hidden: !areExplorerButtonsDisabled
            })}
          >
            <Button
              // On narrow phone-browser viewports collapse to an icon-only button
              // so all three buttons stay on one row inside the sheet.
              text={isNarrow ? undefined : t('Copy link')}
              accessibilityLabel={t('Copy link')}
              onPress={handleCopyTransaction}
              type="primary"
              size="small"
              disabled={areExplorerButtonsDisabled}
              hasBottomSpacing={false}
              style={[styles.footerButton, isNarrow && styles.footerCopyIconButtonNarrow]}
              childrenPosition="left"
            >
              <CopyIcon style={isNarrow ? undefined : spacings.mrTy} width={16} height={16} />
            </Button>
          </View>
        </View>
      </View>
    </View>
  )
}

export default Footer
